import AppAvatar from '@app/components/ui/Avatar';
import { getInitials } from '@app/utils/extractFullnameInitials';
import { Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { FiCalendar, FiPackage, FiRepeat } from 'react-icons/fi';
import { VisitAssignmentBatchAddedNotificationType } from '../../types/notifications';
import { notificationSentAtFormattedDate } from '../../utils';
import { inDisplayZone } from '@app/utils/dates/displayTimezone';

type AssignmentBatchNotificationCardProps = {
  notificationDetails: VisitAssignmentBatchAddedNotificationType;
  onClick: () => void;
};

/**
 * One assignment covering many visits.
 *
 * `UpdatedVisitNotificationCard` cannot render this: its metadata is a single
 * visit, and a batch deliberately carries no `visitId` — there is no one visit
 * for the card to be about. What the reader needs instead is the size of the
 * commitment (how many visits, over what span) and what kind of arrangement it
 * came from, which is what `batchKind` says.
 */
const AssignmentBatchNotificationCard = ({ notificationDetails, onClick }: AssignmentBatchNotificationCardProps) => {
  const { t } = useTranslation();
  const meta = notificationDetails.metadata?.[0];

  const caregiverFullname = meta?.caregiverNames?.[0] || '';
  const caregiverInitials = getInitials(caregiverFullname, { fallback: '' });

  const clientFullName = [meta?.clientFirstName, meta?.clientLastName].filter(Boolean).join(' ');

  const isPackage = meta?.batchKind === 'package';
  const isRecurring = meta?.batchKind === 'recurring';

  const icon = isPackage ? <FiPackage size={22} /> : isRecurring ? <FiRepeat size={22} /> : <FiCalendar size={22} />;
  const kindLabel = isPackage
    ? t('notifications-tag-assignment-package')
    : isRecurring
      ? t('notifications-tag-assignment-recurring')
      : t('notifications-tag-assignment-multiple');

  // Dates are shown only when both bounds are present: a half-rendered span
  // ("from — to 12 Nov") reads as a data error rather than as a missing field.
  const span =
    meta?.firstVisitStartTime && meta?.lastVisitStartTime
      ? `${inDisplayZone(meta.firstVisitStartTime).format('DD MMM YYYY')} — ${inDisplayZone(meta.lastVisitStartTime).format('DD MMM YYYY')}`
      : null;

  return (
    <div
      onClick={onClick}
      className={
        'flex items-start gap-4 bg-white shadow-sm rounded-[10px] p-4 border border-gray-200 hover:shadow-md transition-shadow cursor-pointer'
      }
    >
      {caregiverFullname ? (
        <div className="h-full flex items-center justify-center">
          <AppAvatar isSelected={true} fullName={caregiverFullname} value={caregiverInitials} size={'large'} />
        </div>
      ) : (
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100 text-indigo-700">
          {icon}
        </div>
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between">
          <div className="min-w-0 mb-1">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-gray-900 line-clamp-2">{notificationDetails.title}</h3>
              <Tag color="geekblue">{kindLabel}</Tag>
            </div>

            <p className="mt-1 text-sm text-gray-700 line-clamp-3">{notificationDetails.message}</p>
          </div>

          <div className="ml-4 flex shrink-0 flex-col items-end gap-4">
            <span
              className={`inline-block h-2.5 w-2.5 rounded-full ${notificationDetails.isRead ? 'bg-gray-300' : 'bg-indigo-600'}`}
            />

            <div className="text-right text-xs text-gray-500">
              {t('notifications-sent-at', { time: notificationSentAtFormattedDate(notificationDetails.sentAt) })}
            </div>
          </div>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          {typeof meta?.visitCount === 'number' && (
            <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
              {t('notifications-assignment-visit-count', { count: meta.visitCount })}
            </div>
          )}

          {span && <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">{span}</div>}

          {isPackage && meta?.lineName && (
            <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
              {meta.lineName}
            </div>
          )}
        </div>

        {clientFullName && (
          <div className="mt-2 flex gap-1">
            <span className="font-small text-gray-500">{t('notifications-patient', { name: clientFullName })}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssignmentBatchNotificationCard;
